import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Star, Calculator } from "lucide-react";
import DisclosureBar from "../components/site/DisclosureBar";
import VerdictBox from "../components/site/VerdictBox";
import ProsCons from "../components/site/ProsCons";
import PricingTable from "../components/site/PricingTable";
import ScoreBars from "../components/site/ScoreBars";
import EmailOptin from "../components/site/EmailOptin";
import { FadeUp, MaskedLines } from "../components/site/Reveal";
import { getPlatform } from "../data/platforms";
import { REVIEWS } from "../data/reviews";

const Chapter = ({ id, num, title, children }) => (
  <section id={id} className="scroll-mt-28 border-t border-white/[0.07] pt-12" data-testid={`chapter-${id}`}>
    <FadeUp>
      <div className="flex items-baseline gap-5">
        <span className="font-serif-accent text-4xl italic text-volt/50">{num}</span>
        <h2 className="font-display text-3xl font-black tracking-tighter">{title}</h2>
      </div>
    </FadeUp>
    <div className="mt-8">{children}</div>
  </section>
);

const P = ({ children }) => <p className="mb-5 text-base leading-[1.85] text-white/65">{children}</p>;

const TOC = ({ chapters, active }) => (
  <nav className="glass rounded-2xl p-6" data-testid="review-toc">
    <p className="overline-label mb-4">In This Review</p>
    <ul className="space-y-1">
      {chapters.map((c) => (
        <li key={c.id}>
          <a
            href={`#${c.id}`}
            data-testid={`toc-${c.id}`}
            className={`block rounded-lg px-3 py-2 text-sm transition-colors ${active === c.id ? "bg-volt/10 font-semibold text-volt" : "text-white/55 hover:bg-white/5 hover:text-white"}`}
          >
            <span className="mr-2 font-serif-accent italic text-white/30">{c.num}</span>
            {c.title}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

const ReviewPage = () => {
  const { slug } = useParams();
  const platform = getPlatform(slug);
  const review = REVIEWS[slug];
  const [active, setActive] = useState("summary");

  useEffect(() => {
    if (platform) document.title = review.metaTitle;
    window.scrollTo(0, 0);
  }, [slug, platform, review]);

  useEffect(() => {
    const ids = CHAPTERS.map((c) => c.id);
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-20% 0px -70% 0px" }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [slug]);

  if (!platform || !review) {
    return (
      <main className="mx-auto max-w-3xl px-6 pt-48 pb-32 text-center" data-testid="review-not-found">
        <h1 className="font-display text-4xl font-black">Review not found</h1>
        <Link to="/" className="mt-6 inline-block text-volt underline">Back to all reviews</Link>
      </main>
    );
  }

  return (
    <main>
      <div className="pt-[76px]"><DisclosureBar /></div>
      <header className="mx-auto max-w-6xl px-6 pt-16 pb-6">
        <p className="overline-label mb-5" data-testid="review-overline">In-Depth Review · Updated July 2026 · 12 min read</p>
        <h1 className="max-w-3xl font-display text-5xl font-black leading-[1.05] tracking-tighter" data-testid="review-title">
          <MaskedLines lines={[`${platform.name} Review 2026:`]} />
          <MaskedLines delay={0.15} lines={["The Honest Breakdown"]} lineClassName="font-serif-accent font-medium italic text-volt" />
        </h1>
        <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-white/50">
          <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-volt text-volt" /><strong className="text-white">{platform.rating}</strong>/5 — {platform.badge}</span>
          <span>{platform.trial}</span>
          <span>From {platform.startingPrice}/mo</span>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-14 px-6 pb-28 pt-6 lg:grid-cols-[300px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-6">
            <TOC chapters={CHAPTERS} active={active} />
            <VerdictBox platform={platform} />
          </div>
        </aside>

        <article className="min-w-0 max-w-3xl space-y-16" data-testid="review-article">
          <Chapter id="summary" num="01" title="Executive Summary">
            <P>{platform.quickVerdict}</P>
            <div className="my-8 rounded-2xl border border-white/[0.07] bg-cardink p-7">
              <p className="overline-label mb-6">Tested &amp; Scored</p>
              <ScoreBars scores={platform.scores} />
            </div>
            <ProsCons pros={platform.pros} cons={platform.cons} />
          </Chapter>

          <Chapter id="overview" num="02" title="History & Platform Overview">
            {review.overview.map((p, i) => <P key={i}>{p}</P>)}
          </Chapter>

          <Chapter id="pricing" num="03" title="Pricing Breakdown">
            <PricingTable tiers={platform.pricingTiers} platformName={platform.name} />
            <div className="mt-8">
              {review.pricingNotes.map((p, i) => <P key={i}>{p}</P>)}
              <p className="text-xs text-white/35">Prices researched July 2026 for the smallest contact band. Confirm current pricing on the official {platform.name} site.</p>
            </div>
          </Chapter>

          <Chapter id="features" num="04" title="Feature Deep-Dive">
            {review.features.map((f, i) => (
              <div key={i} className="mb-10">
                <h3 className="mb-4 font-display text-xl font-bold text-volt">{f.subtitle}</h3>
                {f.body.map((p, j) => <P key={j}>{p}</P>)}
              </div>
            ))}
          </Chapter>

          <Chapter id="integrations" num="05" title="Integration Capabilities">
            <div className="mb-8 flex flex-wrap gap-2.5" data-testid="integration-chips">
              {platform.integrations.map((name) => (
                <span key={name} className="rounded-full border border-white/10 bg-cardink px-4 py-1.5 text-xs font-medium text-white/70">{name}</span>
              ))}
            </div>
            {review.integrationsNotes.map((p, i) => <P key={i}>{p}</P>)}
          </Chapter>

          <Chapter id="support" num="06" title="Customer Support Quality">
            {review.support.map((p, i) => <P key={i}>{p}</P>)}
          </Chapter>

          <Chapter id="ideal-for" num="07" title={`Who ${platform.name} Is Ideal For`}>
            <div className="grid gap-4 sm:grid-cols-2">
              {platform.idealFor.map((use, i) => (
                <div key={i} className="rounded-xl border border-white/[0.07] bg-cardink p-5 text-sm leading-relaxed text-white/70" data-testid={`ideal-${i}`}>
                  <span className="mb-2 block font-serif-accent text-2xl italic text-volt/60">{String(i + 1).padStart(2, "0")}</span>
                  {use}
                </div>
              ))}
            </div>
          </Chapter>

          <Chapter id="roi" num="08" title="ROI Calculation Example">
            <div className="rounded-2xl border border-volt/25 bg-cardink p-8" data-testid="roi-callout">
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5 text-volt" />
                <p className="font-display font-bold">{review.roi.scenario}</p>
              </div>
              <ul className="mt-5 space-y-2">
                {review.roi.lines.map((l, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-white/65">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-volt" />{l}
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-volt/20 pt-5 font-serif-accent text-xl italic leading-relaxed text-volt/90">{review.roi.math}</p>
              <p className="mt-4 text-xs text-white/35">{review.roi.note}</p>
            </div>
          </Chapter>

          <Chapter id="verdict" num="09" title="Verdict & Recommendation">
            <P>{review.verdict}</P>
            <div className="mt-8 flex flex-col items-start gap-4 rounded-2xl border border-white/[0.08] bg-cardink p-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-display text-xl font-bold">Ready to try {platform.name}?</p>
                <p className="mt-1 text-sm text-white/50">{platform.trial} · No credit card required</p>
              </div>
              <a href={platform.affiliateUrl} target="_blank" rel="noopener noreferrer sponsored" data-testid="review-bottom-cta" className="flex items-center gap-2 rounded-full bg-volt px-7 py-3.5 text-sm font-bold text-ink transition-colors hover:bg-volt-hover">
                Start Free Trial <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <div className="mt-10">
              <p className="overline-label mb-4">Still deciding?</p>
              <EmailOptin source={`review-${slug}`} compact />
            </div>
          </Chapter>
        </article>
      </div>
    </main>
  );
};

const CHAPTERS = [
  { id: "summary", num: "01", title: "Executive Summary" },
  { id: "overview", num: "02", title: "History & Overview" },
  { id: "pricing", num: "03", title: "Pricing Breakdown" },
  { id: "features", num: "04", title: "Feature Deep-Dive" },
  { id: "integrations", num: "05", title: "Integrations" },
  { id: "support", num: "06", title: "Customer Support" },
  { id: "ideal-for", num: "07", title: "Ideal For" },
  { id: "roi", num: "08", title: "ROI Example" },
  { id: "verdict", num: "09", title: "Verdict" },
];

export default ReviewPage;
